/**
 * Create or edit a workshop (name, address, email, phone).
 */
import { useState, type SubmitEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Dialog } from '@/shared/ui/Dialog';
import { createWorkshop, updateWorkshop, type WorkshopRecord } from './api';
import {
  emptyWorkshopForm,
  validateWorkshopForm,
  workshopToForm,
  type WorkshopFormErrors,
  type WorkshopFormValues,
} from './workshop-form';
import { kebabCase } from '@/shared/lib/kebab-case';

export interface WorkshopDialogProps {
  item: WorkshopRecord | null;
  onClose: (result: 'Confirm' | 'Cancel') => void;
}

const FIELDS: {
  key: keyof WorkshopFormValues;
  label: string;
  placeholder: string;
  multiline?: boolean;
}[] = [
  { key: 'name', label: 'Workshop Name', placeholder: 'Name' },
  {
    key: 'fullAddress',
    label: 'Full address',
    placeholder: 'Full address',
    multiline: true,
  },
  { key: 'email', label: 'Email', placeholder: 'Email' },
  { key: 'phone', label: 'Phone', placeholder: 'Phone' },
];

export function WorkshopDialog({
  item,
  onClose,
}: Readonly<WorkshopDialogProps>) {
  const creating = !item;
  const [values, setValues] = useState<WorkshopFormValues>(() =>
    item ? workshopToForm(item) : emptyWorkshopForm(),
  );
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<WorkshopFormErrors>({});
  // Red border once a field has been edited and is invalid.
  const [dirty, setDirty] = useState<Set<keyof WorkshopFormValues>>(
    () => new Set(),
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const set = (key: keyof WorkshopFormValues, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setDirty((prev) => new Set(prev).add(key));
  };
  const liveErrors = validateWorkshopForm(values);

  const save = useMutation({
    mutationFn: () =>
      item
        ? updateWorkshop({ id: item.id, ...values })
        : createWorkshop({ ...values }),
    onSuccess: () => onClose('Confirm'),
    onError: () =>
      setErrorMessage(
        creating ? 'Failed to create workshop' : 'Failed to update workshop',
      ),
  });

  const onSubmit = (event: SubmitEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setErrorMessage(null);
    const nextErrors = validateWorkshopForm(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setErrorMessage('Please fix the errors');
      return;
    }
    save.mutate();
  };

  return (
    <Dialog
      title={creating ? 'Workshop creation' : 'Workshop edit'}
      onClose={() => onClose('Cancel')}
      width="500px"
      testId="workshop-dialog"
    >
      <form className="form popin-form" onSubmit={onSubmit} noValidate>
        {FIELDS.map(({ key, label, placeholder, multiline }) => {
          const id = `workshop-${key}`;
          const testId = `workshop-form-${kebabCase(key)}`;
          const className = `field ${dirty.has(key) && liveErrors[key] ? 'invalid' : ''}`;
          return (
            <div className="mb-2" key={key}>
              <label className="field-label" htmlFor={id}>
                {label}
              </label>
              {multiline ? (
                <textarea
                  id={id}
                  data-testid={testId}
                  className={className}
                  name={key}
                  placeholder={placeholder}
                  value={values[key]}
                  onChange={(event) => set(key, event.target.value)}
                />
              ) : (
                <input
                  id={id}
                  data-testid={testId}
                  className={className}
                  name={key}
                  placeholder={placeholder}
                  value={values[key]}
                  onChange={(event) => set(key, event.target.value)}
                />
              )}
              <small
                hidden={!(submitted && errors[key])}
                data-testid={`${testId}-error`}
              >
                {errors[key]}
              </small>
            </div>
          );
        })}
        {errorMessage && (
          <div
            className="alert mb-0"
            role="alert"
            data-testid="workshop-form-error"
          >
            <span className="error-msg">{errorMessage}</span>
          </div>
        )}
        <div className="mt-6 flex justify-center">
          <button
            className="button button-form"
            type="submit"
            disabled={save.isPending}
            data-testid="workshop-form-save-button"
          >
            Save
          </button>
        </div>
      </form>
    </Dialog>
  );
}
